import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlagContext } from '@eu-data-act/react-euda-commons/dist/index';

import { useTheme } from '@veneer/theme';
import { Button } from '@veneer/core';

import DeviceList, { NoDevices } from '../device-list';
import { Divider } from '../common';

import { LoadCategory, Title } from './styled';

import { CategoryType, DeviceType, DeviceSubType } from '../../types/devices';

import { RootState } from '../../store';

import {
  useDeviceTypeMapping,
  extractDeviceTypeSubTypePairs
} from '../../hooks/useDeviceTypeMapping';
import { updateCachedCategory } from '../../utils/cached-category';
import { DeviceTypeMap } from '../../hooks/useDeviceCategoryMapping';
import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import { useTabIndex } from '../../contexts/tab-index';
import {
  postDeviceMappingCleanup,
  postDeviceMappingRequest
} from '../../features/devices/devices-slice';

import RequestDataModal from './request-data-modal';
import { URLS } from 'src/utils/urls';
import { OperationStatus } from 'src/types';

type CategoryProps = {
  dataTestId?: string;
  type: CategoryType;
  subTypeMap?: DeviceTypeMap;
  isLastCategory?: boolean;
};

const CategoryItem = (props: CategoryProps): React.ReactElement => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { translate } = useDependencyManagerContext();
  const { reserveTabIndicesForCategory, getTabIndexForCategoryDevice } =
    useTabIndex();
  const releasePlatformHpAnalytics = useFeatureFlagContext();

  const [openPCDataModal, setOpenPCDataModal] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState({
    serialNumber: '',
    biosUuid: ''
  });
  const { type, subTypeMap, dataTestId, isLastCategory } = props;

  const { user } = useSelector((state: RootState) => state.UserReducer);
  const userEmail = user?.email?.value;

  const [
    isCached,
    devices,
    deviceMappingRequestStatus,
    deviceMappingRequestError
  ] = useDeviceTypeMapping(type, subTypeMap);

  const deviceCount = Array.isArray(devices) ? devices.length : 0;
  const buttonsPerDevice = 3;
  const categoryStartTabIndex = reserveTabIndicesForCategory(
    deviceCount * buttonsPerDevice
  );

  const requestPCDataTabIndex =
    type === CategoryType.PERSONAL_SYSTEMS ? categoryStartTabIndex : undefined;
  const loadCategoryTabIndex =
    !isCached && type !== CategoryType.PERSONAL_SYSTEMS
      ? categoryStartTabIndex
      : undefined;

  const handleCategoryLoad = (): void => {
    updateCachedCategory(type, userEmail, Date.now());
  };

  const handleRetryDeviceMapping = (): void => {
    dispatch(postDeviceMappingCleanup());

    if (subTypeMap && Object.keys(subTypeMap).length > 0) {
      const deviceTypeSubTypePairs = extractDeviceTypeSubTypePairs(subTypeMap);

      if (deviceTypeSubTypePairs.length > 0) {
        // Post device mapping requests for each device type/subtype pair
        deviceTypeSubTypePairs.forEach((pair) => {
          const payload = {
            device_type: pair.deviceType,
            device_sub_type: pair.deviceSubType
          };
          dispatch(postDeviceMappingRequest({ type, payload }));
        });
      } else {
        // Fallback: handle subTypeMap keys directly
        Object.keys(subTypeMap).forEach((key) => {
          const payload = {
            device_type: key.split('_')[0] as DeviceType,
            device_sub_type: key.split('_')[1] as DeviceSubType
          };
          dispatch(postDeviceMappingRequest({ type, payload }));
        });
      }
    }
  };

  const {
    checkButtonAnalyticsAttributes,
    psRequestButtonAnalyticsAttributes,
    largeFormatLearnMoreAnalyticsAttributes
  } = useMemo(
    () => ({
      checkButtonAnalyticsAttributes: releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `check-${type}-button`,
            'data-gtm-value': `check-devices-of-type-${type}`
          }
        : {},
      psRequestButtonAnalyticsAttributes: releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `request-data-${type}-button`,
            'data-gtm-value': `request-data-modal-${type}`
          }
        : {},
      largeFormatLearnMoreAnalyticsAttributes: releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'linkClick',
            'data-gtm-id': `learn-more-${type}-button`,
            'data-gtm-value': `learn-more-about-${type}-printers`
          }
        : {}
    }),
    [releasePlatformHpAnalytics, type]
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Title $veneerTheme={theme}>
          {translate(`components.device-category.categories.${type}`)}
        </Title>

        {type === CategoryType.PERSONAL_SYSTEMS && (
          <Button
            appearance="ghost"
            small
            onClick={() => setOpenPCDataModal(true)}
            data-testid={`react-device-management-mfe-category-request-${type}-data-button`}
            tabIndex={requestPCDataTabIndex}
            style={{ marginBottom: theme.layout.size4 }}
            {...psRequestButtonAnalyticsAttributes}
          >
            {translate('components.device-category.button.request-pc-data')}
          </Button>
        )}
      </div>

      <LoadCategory
        $veneerTheme={theme}
        data-testid={`${dataTestId}-${isCached ? 'cached' : 'not-cached'}`}
      >
        {isCached ? (
          <DeviceList
            status={deviceMappingRequestStatus}
            type={type}
            devices={devices}
            error={deviceMappingRequestError}
            categoryStartTabIndex={categoryStartTabIndex}
            getTabIndexForCategoryDevice={getTabIndexForCategoryDevice}
            retryAction={handleRetryDeviceMapping}
          />
        ) : type === CategoryType.PERSONAL_SYSTEMS ? (
          <NoDevices type={type} />
        ) : (
          <Button
            appearance="ghost"
            data-testid={`react-device-management-mfe-category-load-button-${type}`}
            onClick={handleCategoryLoad}
            tabIndex={loadCategoryTabIndex}
            {...checkButtonAnalyticsAttributes}
          >
            {translate('components.device-category.button.load-category', {
              type: translate(`components.device-category.categories.${type}`)
            })}
          </Button>
        )}

        {type === CategoryType.LARGE_FORMAT &&
          isCached &&
          Array.isArray(devices) &&
          devices.length === 0 &&
          deviceMappingRequestStatus !== OperationStatus.RUNNING &&
          !deviceMappingRequestError && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Button
                appearance="ghost"
                style={{ marginTop: theme.layout.size2 }}
                onClick={() => {
                  window.open(URLS.learnMoreAboutLargeFormatPrinters, '_blank');
                }}
                {...largeFormatLearnMoreAnalyticsAttributes}
              >
                {translate(
                  'components.device-category.learn-more-connection.large-format-printer'
                )}
              </Button>
            </div>
          )}
      </LoadCategory>

      <Divider
        style={{
          margin: isLastCategory
            ? `calc(${theme.layout.size8} + ${theme.layout.size2}) 0 0 0`
            : `calc(${theme.layout.size8} + ${theme.layout.size2}) 0`
        }}
      />

      <RequestDataModal
        open={openPCDataModal}
        deviceDetails={deviceDetails}
        setDeviceDetails={setDeviceDetails}
        openModal={setOpenPCDataModal}
      />
    </div>
  );
};

export default CategoryItem;
